import { useNavigate } from "react-router-dom"
import { Box, Button, Heading, Radio, RadioGroup, Stack, FormControl, FormLabel, Input, Text, Switch } from "@chakra-ui/react"
import { useFormik } from "formik"
import { useState, useEffect } from "react"

import { useMapFromSearchParams } from "./hooks/useMapFromSearchParams"

import CreatorMap from "../../components/creator/CreatorMap"
import { MapProvider } from "../../contexts/MapContext"
import CreatorMapExportButton from "../../components/creator/CreatorMapExportButton"
import { Helmet } from "react-helmet-async"
import LoadingOverlay from "../../components/LoadingOverlay"
import StatusOverlay from "../../components/StatusOverlay"
import { useSelector } from "react-redux"
import FormElement from "../../components/ui/FormElement"
import FormHeading from "../../components/ui/FormHeading"


export default function CreatorExport() {

    const { mapBeingHandled: mapBeingExported, toDisplay } = useMapFromSearchParams()

    const { isErrorGetting, message } = useSelector(state => state.creator)

    const navigate = useNavigate()

    const formik = useFormik({
        initialValues: {
            'width': 800,
            'height': 800,
        },
    })

    const [aspectRatio, setAspectRatio] = useState('1:1')
    const [useCustomHeight, setUseCustomHeight] = useState(false)
    const [exportQuality, setExportQuality] = useState('hq')

    useEffect(() => {
        if (useCustomHeight)
            return

        const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number)
        const newHeight = formik.values.width * heightRatio / widthRatio
        formik.setFieldValue('height', Math.round(newHeight))
    }, [formik.values.width, aspectRatio, useCustomHeight])

    const MyHelmet = () => {
        return <Helmet>
          <title>Export map - Creator (Preview) | TrainLookout</title>
          <meta name="description" content="Create elegant rail maps." />
          <meta name="keywords" content="creator, creator mode, rail, journey, record, history, train, trainlookout, train lookout, trainlookout.com, train lookout.com" />
          <link rel="canonical" href="/creator/edit" />
        </Helmet>
    }
    
    
    if (!toDisplay) {
        return (
            <>
                <MyHelmet />
                <LoadingOverlay type="full"/>
            </>
        )
    }

    if (isErrorGetting) {
        return (
            <>
                <MyHelmet />
                <StatusOverlay type="modal" text={`Creator mode is currently inaccessible. Please try again later. Error details: ${message}`}/>
            </>
        )
    }

    return (
        <>
            <MapProvider>
                <Box p={4} borderWidth="1px" borderRadius="lg">
                    <Box mb={4}>
                        <Heading as='h3' size='lg' mb={2}>Exporting: {mapBeingExported.name}</Heading>
                        <Text fontSize="md">The exported map will look exactly as in the preview below, except without the zoom control (in the top-right corner).</Text>
                        <Text fontSize="md">Adjust the position, size and aspect ratio below. Select the desired quality and click 'Export' to download your map.</Text>
                    </Box>
                    <Box
                        width={formik.values.width}
                        height={formik.values.height}
                        overflow="auto"
                    >
                        <CreatorMap 
                            creatorMap={mapBeingExported} 
                            isScrollWheelZoomDisabled
                        />
                    </Box>
    
                    <Box>
                        <FormElement>
                            <FormHeading>Size & Aspect Ratio:</FormHeading>
                            
                            <FormControl>
                                <FormLabel>Use custom size:</FormLabel>
                                <Box>
                                    <Switch checked={useCustomHeight} onChange={() => setUseCustomHeight(!useCustomHeight)} />
                                </Box>
    
                                {useCustomHeight && (
                                    <Box>
                                        <FormLabel>Height:</FormLabel>
                                        <Input
                                            type='number'
                                            name='height'
                                            value={formik.values.height}
                                            onChange={formik.handleChange}
                                            min={100}
                                            max={3000}
                                            width={200}
                                        />
                                    </Box>
                                )}
    
                                {!useCustomHeight && (
                                    <RadioGroup
                                        name='aspectRatio'
                                        value={aspectRatio}
                                        onChange={setAspectRatio}
                                    >
                                        <Stack direction='row'>
                                            <Box>
                                                <Text>Square:</Text>
                                                <Stack>
                                                    <Radio value="1:1">1:1</Radio>
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Text>Landscape:</Text>
                                                <Stack>
                                                    <Radio value="4:3">4:3</Radio>
                                                    <Radio value="16:9">16:9</Radio>
                                                    <Radio value="16:10">16:10</Radio>
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Text>Portrait:</Text>
                                                <Stack>
                                                    <Radio value="3:4">3:4</Radio>
                                                    <Radio value="9:16">9:16</Radio>
                                                    <Radio value="10:16">10:16</Radio>
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </RadioGroup>
                                )}
    
                                <FormLabel>Width:</FormLabel>
                                <Input
                                    type='number'
                                    name='width'
                                    value={formik.values.width}
                                    onChange={formik.handleChange}
                                    min={100}
                                    max={3000}
                                    width={200}
                                />
                            </FormControl>
                        </FormElement>

                        <FormElement>
                            <FormHeading>Export quality:</FormHeading>

                            <RadioGroup
                                name='exportQuality'
                                value={exportQuality}
                                onChange={setExportQuality}
                            >
                                <Stack>
                                    <Radio value="standard">Standard</Radio>
                                    <Radio value="hq">HQ</Radio>
                                    <Radio value="ultra-hq">Ultra HQ</Radio>
                                </Stack>        
                            </RadioGroup>
                            
                        </FormElement>
    
                        <FormElement>
                            <CreatorMapExportButton
                                enableWatermark={mapBeingExported.watermark}
                                background={mapBeingExported.background}
                                exportQuality={exportQuality}

                                colorScheme="blue"
                                size="lg"
                            />
    
                            <Button variant='outline' colorScheme="gray" mt={4} onClick={() => navigate('/creator/edit?mapId=' + mapBeingExported._id)}>
                                Back to editing
                            </Button>
                        </FormElement>
                    </Box>
                </Box>
            </MapProvider>
        </>
    )
}